import { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Home.module.css';
import { HomeContext } from './Home.context';
import { InstallBtn } from '../../../views/installation/Installation';
import { Issue, NAVBAR_ICON_URL, FAVORITE_KEY } from '../../../constants';
import config from '../../../config';
import { Icon } from '../../../components/icon/Icon';
import { AppContext } from '../../../App.context';
import { Modal } from '../../../components/modal/Modal';
import { AboutButton } from '../../../components/about/About';
import { EyeIndicator } from '../../../views/drafts/Drafts';
import { TrackingContext as TrackingContext } from '../../../tracking/Tracking.context';
import { Magazines } from './magazines/Magazines';
// import { isUserAuthenticated } from './auth/AuthenticationDialog';

export const Home = () => {
	const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
	const [showInstallation, setShowInstallation] = useState(false);
	const [showLogout, setShowLogout] = useState(false);

	useEffect(() => {
		if (!localStorage.getItem(FAVORITE_KEY)) {
			localStorage.setItem(FAVORITE_KEY, '[]');
		}
	}, []);

	const onClickLogOut = () => {
		console.log(`I click logout`);
	};

	return (
		<HomeContext.Provider
			value={{
				selectedIssue,
				setSelectedIssue,
				setShowInstallation,
				setShowLogout,
				onClickLogOut,
			}}
		>
			<InstallationDialog show={showInstallation} />
			<LogOutDialog show={showLogout} />
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
					<Navbar />
				</div>
			</div>
		</HomeContext.Provider>
	);
};

const Topbar = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { isInstalled } = useContext(AppContext);
	const { selectedIssue, setSelectedIssue, setShowInstallation } =
		useContext(HomeContext);
	if (selectedIssue) {
		return (
			<div className={styles['topbar']}>
				<div>
					<button
						type="button"
						onClick={() => {
							const updatedSearchParams = new URLSearchParams(
								searchParams.toString(),
							);
							updatedSearchParams.set('id', '');
							updatedSearchParams.delete('id');
							setSearchParams(updatedSearchParams.toString());
							setSelectedIssue(null);
						}}
					>
						<Icon name={'arrow-left'} color={'#000'} />
					</button>
				</div>
				<div className={styles['title']}>
					<img src={NAVBAR_ICON_URL} alt="Logo" />
				</div>
				<div></div>
			</div>
		);
	}

	return (
		<div className={styles['topbar']}>
			<div>
				<AboutButton />
				<EyeIndicator />
			</div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<ButtonBar />
				{!isInstalled ? (
					<InstallBtn
						onClick={() => {
							console.log(`I click the button`);
							setShowInstallation(true);
						}}
					/>
				) : null}
			</div>
		</div>
	);
};

function Content() {
	return (
		<div className={styles['content']}>
			<Magazines />
		</div>
	);
}

function Navbar() {
	return (
		<nav>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
		} else {
			root.style.setProperty('--bottom-bar-height', '0px');
		}
	}, [ref]);

	return (
		<div ref={ref} className={styles['button-bar']}>
			<ButtonLogOut />
		</div>
	);
}

const ButtonLogOut = () => {
	const { setShowLogout } = useContext(HomeContext);
	if (!config.app.auth.enabled) {
		return null;
	}

	return (
		<button
			type={'button'}
			title="Log out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				setShowLogout(true);
			}}
			className={'tooltip down'}
		>
			<Icon color="#000" name="logout" />
			Log Out
		</button>
	);
};

const LogOutDialog = ({ show }: DialogProps) => {
	const { sendEvent } = useContext(TrackingContext);
	const { setShowLogout, onClickLogOut } = useContext(HomeContext);
	return (
		<Modal
			classNames={[styles['logout-dialog-container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShowLogout(false);
			}}
		>
			<div className={styles['logout-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Are you sure you want to log out?</h3>
				<button
					type="button"
					onClick={() => {
						sendEvent({
							type: 'BTN_CLICK_LOGOUT',
						});
						setShowLogout(false);
						onClickLogOut();
					}}
				>
					Log Out
				</button>
			</div>
		</Modal>
	);
};

interface DialogProps {
	show: boolean;
}

const InstallationDialog = ({ show }: DialogProps) => {
	const { setShowInstallation } = useContext(HomeContext);
	const { device } = useContext(AppContext);
	const message = device?.isTouchable ? `Add to Home Screen` : `Add to Dock`;
	return (
		<Modal
			classNames={[styles['installation-dialog-container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShowInstallation(false);
			}}
		>
			<div className={styles['installation-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Install App</h3>
				<p>
					Install this app for quick and easy access <br /> when
					you&apos;re on the go.
				</p>
				{isSafari() ? (
					<ol>
						<li>
							Tap the <Share /> button on your screen.
						</li>
						<li>
							Select <b>&lsquo;{message}&rsquo;</b>.
						</li>
						<li>
							Tap <b>&lsquo;Add&rsquo;</b>.
						</li>
					</ol>
				) : (
					<ol>
						<li>
							Tap the Menu icon (three vertical dots)
							<br /> in the top-right corner of your screen.
						</li>
						<li>
							Select <b>&lsquo;Add to Home screen&rsquo;</b>.
						</li>
						<li>
							Press <b>&lsquo;Install&rsquo;</b>.
						</li>
					</ol>
				)}
			</div>
		</Modal>
	);
};

function Share() {
	return (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
			<polyline points="16 6 12 2 8 6"></polyline>
			<line x1="12" y1="2" x2="12" y2="15"></line>
		</svg>
	);
}

function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
